import {property} from "lit/decorators.js";
import {ManagingResources} from "../../../common/lifetime";
import type {EopPopover} from "./popover";
import {resolve} from "../../../container";
import {PageFeatures} from "../pageFeatures";
import {Resolution} from "../../../common/resolution";
import {elementFrom, templateContaining} from "../../../common/utils/html";
import {UnLitElement} from "../../../common/elements";
import type {Nullable} from "../../../bootstrap/common/types";

export abstract class PopoverAnchor extends ManagingResources(UnLitElement) {

    @property({attribute: "content"})
    public contentId: string;
    @property({attribute: "popover-background-color"})
    public background: string;
    @property({attribute: "enforce-close-button", type: Boolean})
    public enforceCloseButton: boolean = false;

    protected popoverElement: EopPopover;
    private modalDialog: Nullable<HTMLDialogElement>;

    public constructor(
        private pageFeatures: PageFeatures = resolve(PageFeatures),
        private resolution: Resolution = resolve(Resolution)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();

        this.resolution.onWindowResize(() => this.setPopoverPosition(), this);

        const templateHTML = this.resolveTemplateElement(this.contentId).innerHTML;
        const template = templateContaining(templateHTML);
        template.setAttribute("data-id", "content");

        const popoverHtml = `<eop-popover content="content" background=${this.background} ${(this.enforceCloseButton ? "enforce-close-button" : "")}></eop-popover>`;

        this.modalDialog = this.closest("dialog");
        if (this.modalDialog) {
            this.popoverElement = this.modalDialog.querySelector(".dialog-panel")!.appendChild(elementFrom(popoverHtml));
            this.popoverElement.setAttribute("in-layer", "");
        } else {
            this.popoverElement = this.pageFeatures.insert<EopPopover>(elementFrom(popoverHtml));
        }
        this.popoverElement.appendChild(template);
        this.popoverElement.onParent(this);
    }

    public abstract getAnchorElement(): HTMLElement;

    public setPopoverPosition(): void {
        const currentAnchorElementSize = this.computeCurrentAnchorElementSize();

        const parentHeightProperty = this.popoverElement.style.getPropertyValue("--parent-height");
        const parentWidthProperty = this.popoverElement.style.getPropertyValue("--parent-width");
        if (parentHeightProperty === "0px" || parentWidthProperty === "0px" || parentHeightProperty === "" || parentWidthProperty === "") {
            this.popoverElement.style.setProperty("--parent-height", currentAnchorElementSize.height + "px");
            this.popoverElement.style.setProperty("--parent-width", currentAnchorElementSize.width + "px");
        }

        const topOffset = this.modalDialog ? this.getAnchorElement().getBoundingClientRect().top : this.getAnchorElement().topOffset();
        const leftOffset = this.modalDialog ? this.getAnchorElement().getBoundingClientRect().left : this.getAnchorElement().leftOffset();
        this.popoverElement.style.setProperty("--parent-center-top-offset", topOffset + 0.5 * currentAnchorElementSize.height + "px");
        this.popoverElement.style.setProperty("--parent-center-left-offset", leftOffset + 0.5 * currentAnchorElementSize.width + "px");
    }

    private computeCurrentAnchorElementSize(): { width: number; height: number } {
        return {
            width: this.getAnchorElement().getBoundingClientRect().width, // visible width, not the same as clientWidth
            height: this.getAnchorElement().getBoundingClientRect().height // visible height, not the same as clientHeight
        };
    }

    public disconnectedCallback(): void {
        this.popoverElement.remove();
        super.disconnectedCallback();
    }
}